import { RefObject, useLayoutEffect, useRef, useState } from "react";

// Space below the scrollable table body that must stay on screen: the
// pagination row plus the "Show entries" footer and card/page padding.
const RESERVE = 96;
const MIN_BODY = 120;

/**
 * Sizes an antd table's scrollable body so the whole page fits the viewport
 * (no browser scrollbar) while the header, stats and pagination stay fixed.
 *
 * The body height is the smaller of:
 *  - the height of the rows actually on the current page, and
 *  - the space left between the body's top and the bottom of the viewport.
 *
 * When the rows fit, the body matches the content exactly (no internal scroll —
 * e.g. tables with few rows). When they don't, it caps at the available space
 * and scrolls internally instead of pushing the page taller.
 *
 * Returns a ref to attach to the table wrapper and the computed body height to
 * pass as antd's `scroll={{ y }}`.
 */
export function useTableBodyHeight(
  active: boolean,
  pageSize: number,
  rowCount: number,
  fallbackRowHeight = 56,
): [RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [scrollY, setScrollY] = useState<number>(pageSize * fallbackRowHeight);

  useLayoutEffect(() => {
    if (!active) return;

    function recompute() {
      const el = ref.current;
      if (!el) return;

      const bodyEl = el.querySelector<HTMLElement>(".ant-table-body");
      let bodyTop: number;
      if (bodyEl) {
        bodyTop = bodyEl.getBoundingClientRect().top;
      } else {
        const headEl = el.querySelector<HTMLElement>(".ant-table-thead");
        bodyTop = headEl
          ? headEl.getBoundingClientRect().bottom
          : el.getBoundingClientRect().top + 45;
      }

      const available = window.innerHeight - bodyTop - RESERVE;

      const rowEl = el.querySelector<HTMLElement>(
        ".ant-table-tbody tr.ant-table-row",
      );
      const rowHeight = rowEl?.getBoundingClientRect().height || fallbackRowHeight;
      const rows = Math.max(1, Math.min(pageSize, rowCount || pageSize));
      const contentHeight = rows * rowHeight + 2;

      setScrollY(Math.max(MIN_BODY, Math.min(contentHeight, available)));
    }

    recompute();
    const raf = requestAnimationFrame(recompute);
    window.addEventListener("resize", recompute);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", recompute);
    };
  }, [active, pageSize, rowCount, fallbackRowHeight]);

  return [ref, scrollY];
}
